import { t } from "../../i18n";
import type { ActivityGenerationSettings } from "@lumen/shared";
import type { ComposerActivity } from "./Composer";

export function ComposerModel3dSettings({
  activity,
  settings,
  update,
  isTripoRigging,
  isTripoAnimation,
  isTripoModel,
  isTripoGeneration,
  isTripoHSeries,
  isTripoP1,
  isScenarioModel,
}: {
  activity: Exclude<ComposerActivity, "ask">;
  settings: ActivityGenerationSettings;
  update: (patch: Partial<ActivityGenerationSettings>) => void;
  isTripoRigging: boolean;
  isTripoAnimation: boolean;
  isTripoModel: boolean;
  isTripoGeneration: boolean;
  isTripoHSeries: boolean;
  isTripoP1: boolean;
  isScenarioModel: boolean;
}) {
  return (
    <>
      {activity === "model3d" && !isScenarioModel && (
        <div className="space-y-3 rounded-lg border border-lumen-200 bg-lumen-50/50 p-3 dark:border-lumen-900/50 dark:bg-lumen-950/20">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="label">{t("Target engine")}</span>
              <select
                className="field"
                value={settings.engine ?? ""}
                onChange={(event) =>
                  update({
                    engine: (event.target.value ||
                      undefined) as typeof settings.engine,
                  })
                }
              >
                <option value="">{t("Generic")}</option>
                <option value="unity">{t("Unity")}</option>
                <option value="unreal">{t("Unreal")}</option>
                <option value="godot">{t("Godot")}</option>
                <option value="web">{t("Web")}</option>
              </select>
            </label>
            <label className="block">
              <span className="label">{t("Export format")}</span>
              <select
                className="field"
                value={settings.outputFormat ?? ""}
                onChange={(event) =>
                  update({ outputFormat: event.target.value || undefined })
                }
              >
                <option value="">{t("Engine default")}</option>
                <option value="glb">{t("GLB")}</option>
                {isTripoGeneration && <option value="gltf">{t("GLTF")}</option>}
                <option value="fbx">{t("FBX")}</option>
                {!isTripoRigging && !isTripoAnimation && (
                  <>
                    <option value="obj">{t("OBJ")}</option>
                    <option value="usdz">{t("USDZ")}</option>
                    <option value="stl">{t("STL")}</option>
                    {isTripoGeneration && (
                      <option value="3mf">{t("3MF")}</option>
                    )}
                  </>
                )}
              </select>
            </label>
            <label className="block">
              <span className="label">{t("Topology")}</span>
              <select
                className="field"
                value={settings.topology ?? ""}
                onChange={(event) =>
                  update({
                    topology: (event.target.value ||
                      undefined) as typeof settings.topology,
                  })
                }
              >
                <option value="">{t("Default")}</option>
                <option value="triangle">{t("Triangles")}</option>
                <option
                  value="quad"
                  disabled={isTripoP1 || (isTripoModel && !isTripoHSeries)}
                >
                  {t("Quads")}
                </option>
              </select>
            </label>
            <label className="block">
              <span className="label">{t("Target polygons")}</span>
              <input
                type="number"
                className="field"
                min={isTripoP1 ? 48 : 100}
                max={isTripoP1 ? 20000 : 300000}
                value={settings.targetPolycount ?? ""}
                placeholder={t("Provider default")}
                onChange={(event) =>
                  update({
                    targetPolycount: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
              />
            </label>
          </div>
          <label className="block">
            <span className="label">{t("Texture prompt")}</span>
            <input
              className="field"
              value={settings.texturePrompt ?? ""}
              placeholder={t("Optional PBR/material guidance")}
              onChange={(event) =>
                update({ texturePrompt: event.target.value || undefined })
              }
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="label">{t("Texture resolution")}</span>
              <select
                className="field"
                value={settings.textureResolution ?? ""}
                onChange={(event) =>
                  update({
                    textureResolution: (event.target.value ||
                      undefined) as typeof settings.textureResolution,
                    pbr: event.target.value ? true : settings.pbr,
                  })
                }
              >
                <option value="">{t("No texture pass")}</option>
                <option value="2k">{t("2K PBR")}</option>
                <option value="4k">{t("4K PBR")}</option>
                <option value="8k">{t("8K PBR")}</option>
              </select>
            </label>
            {!isTripoRigging && !isTripoAnimation && (
              <label className="block">
                <span className="label">{t("Animation action ID")}</span>
                <input
                  type="number"
                  className="field"
                  min={0}
                  value={settings.animationActionId ?? ""}
                  placeholder={t("Optional; enables rigging")}
                  onChange={(event) =>
                    update({
                      animationActionId: event.target.value
                        ? Number(event.target.value)
                        : undefined,
                      rig: event.target.value ? true : settings.rig,
                    })
                  }
                />
              </label>
            )}
          </div>
          {isTripoRigging && (
            <div className="space-y-2 rounded-lg border border-lumen-300 bg-white/60 p-2 dark:border-lumen-800 dark:bg-black/10">
              <p className="text-[11px] text-stone-500">
                {t(
                  "Attach a GLB, GLTF, FBX, OBJ, or STL character model. Lumen runs Tripo Rig Check before creating the skeleton.",
                )}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="label">{t("Rig type")}</span>
                  <select
                    className="field"
                    value={settings.rigType ?? ""}
                    onChange={(event) =>
                      update({
                        rigType: (event.target.value ||
                          undefined) as typeof settings.rigType,
                      })
                    }
                  >
                    <option value="">{t("Auto detect")}</option>
                    <option value="biped">{t("Biped")}</option>
                    <option value="quadruped">{t("Quadruped")}</option>
                    <option value="hexapod">{t("Hexapod")}</option>
                    <option value="octopod">{t("Octopod")}</option>
                    <option value="avian">{t("Avian")}</option>
                    <option value="serpentine">{t("Serpentine")}</option>
                    <option value="aquatic">{t("Aquatic")}</option>
                  </select>
                </label>
                <label className="block">
                  <span className="label">{t("Bone naming")}</span>
                  <select
                    className="field"
                    value={settings.rigSpec ?? ""}
                    onChange={(event) =>
                      update({
                        rigSpec: (event.target.value ||
                          undefined) as typeof settings.rigSpec,
                      })
                    }
                  >
                    <option value="">{t("Tripo")}</option>
                    <option value="mixamo">{t("Mixamo")}</option>
                  </select>
                </label>
                <label className="col-span-2 block">
                  <span className="label">{t("Rig model")}</span>
                  <select
                    className="field"
                    value={settings.rigModel ?? ""}
                    onChange={(event) =>
                      update({
                        rigModel: (event.target.value ||
                          undefined) as typeof settings.rigModel,
                      })
                    }
                  >
                    <option value="">{t("Auto-select from rig type")}</option>
                    <option value="v1.0-20240301">{t("v1.0 · Biped")}</option>
                    <option value="v2.5-20260210">
                      {t("v2.5 · Creatures")}
                    </option>
                  </select>
                </label>
              </div>
            </div>
          )}
          {isTripoAnimation && (
            <div className="space-y-2 rounded-lg border border-lumen-300 bg-white/60 p-2 dark:border-lumen-800 dark:bg-black/10">
              <p className="text-[11px] text-stone-500">
                {t(
                  "Attach a GLB, GLTF, FBX, OBJ, or STL model. Static models are rigged automatically before the animation is applied.",
                )}
              </p>
              <label className="block">
                <span className="label">{t("Animation preset")}</span>
                <select
                  className="field"
                  value={settings.animationPreset ?? "preset:idle"}
                  onChange={(event) =>
                    update({ animationPreset: event.target.value || undefined })
                  }
                >
                  <option value="preset:idle">{t("Idle")}</option>
                  <option value="preset:walk">{t("Walk")}</option>
                  <option value="preset:run">{t("Run")}</option>
                  <option value="preset:jump">{t("Jump")}</option>
                  <option value="preset:climb">{t("Climb")}</option>
                  <option value="preset:dive">{t("Dive")}</option>
                  <option value="preset:slash">{t("Slash")}</option>
                  <option value="preset:shoot">{t("Shoot")}</option>
                  <option value="preset:hurt">{t("Hurt")}</option>
                  <option value="preset:fall">{t("Fall")}</option>
                  <option value="preset:turn">{t("Turn")}</option>
                  <option value="preset:quadruped:walk">
                    {t("Quadruped walk")}
                  </option>
                </select>
              </label>
              <label className="block">
                <span className="label">{t("Additional animations")}</span>
                <input
                  className="field"
                  value={settings.animationPresets?.join(", ") ?? ""}
                  placeholder={t("Optional; up to 5 comma-separated IDs")}
                  onChange={(event) =>
                    update({
                      animationPresets: event.target.value
                        ? event.target.value
                            .split(",")
                            .map((value) => value.trim())
                            .filter(Boolean)
                            .slice(0, 5)
                        : undefined,
                    })
                  }
                />
              </label>
              <div className="flex flex-wrap gap-4 text-xs">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.inputRigged ?? false}
                    onChange={(event) =>
                      update({ inputRigged: event.target.checked || undefined })
                    }
                  />
                  {t("Input is already rigged")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.bakeAnimation ?? true}
                    onChange={(event) =>
                      update({ bakeAnimation: event.target.checked })
                    }
                  />
                  {t("Bake animation")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.exportWithGeometry ?? true}
                    onChange={(event) =>
                      update({ exportWithGeometry: event.target.checked })
                    }
                  />
                  {t("Export with geometry")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.animateInPlace ?? false}
                    onChange={(event) =>
                      update({
                        animateInPlace: event.target.checked || undefined,
                      })
                    }
                  />
                  {t("Animate in place")}
                </label>
              </div>
            </div>
          )}
          {isTripoGeneration && (
            <div className="space-y-2 rounded-lg border border-lumen-300 bg-white/60 p-2 dark:border-lumen-800 dark:bg-black/10">
              <p className="text-[11px] font-medium text-stone-600 dark:text-stone-300">
                {t("Tripo generation parameters")}
              </p>
              <p className="text-[11px] text-stone-500">
                {t(
                  "Attach one image for image-to-3D, or two/three images for multiview generation. Images are sent in attachment order: front, back, right.",
                )}
              </p>
              <label className="block">
                <span className="label">{t("Negative prompt")}</span>
                <input
                  className="field"
                  maxLength={255}
                  value={settings.negativePrompt ?? ""}
                  placeholder={t("Optional things to avoid")}
                  onChange={(event) =>
                    update({ negativePrompt: event.target.value || undefined })
                  }
                />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <label className="block">
                  <span className="label">{t("Model seed")}</span>
                  <input
                    type="number"
                    className="field"
                    value={settings.modelSeed ?? ""}
                    placeholder={t("Random")}
                    onChange={(event) =>
                      update({
                        modelSeed: event.target.value
                          ? Number(event.target.value)
                          : undefined,
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="label">{t("Image seed")}</span>
                  <input
                    type="number"
                    className="field"
                    value={settings.imageSeed ?? ""}
                    placeholder={t("Random")}
                    onChange={(event) =>
                      update({
                        imageSeed: event.target.value
                          ? Number(event.target.value)
                          : undefined,
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="label">{t("Texture seed")}</span>
                  <input
                    type="number"
                    className="field"
                    value={settings.textureSeed ?? ""}
                    placeholder={t("Random")}
                    onChange={(event) =>
                      update({
                        textureSeed: event.target.value
                          ? Number(event.target.value)
                          : undefined,
                      })
                    }
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="label">{t("Texture quality")}</span>
                  <select
                    className="field"
                    value={settings.textureQuality ?? ""}
                    onChange={(event) =>
                      update({
                        textureQuality: (event.target.value ||
                          undefined) as typeof settings.textureQuality,
                      })
                    }
                  >
                    <option value="">{t("Provider default")}</option>
                    <option value="standard">{t("Standard")}</option>
                    <option value="detailed">{t("Detailed")}</option>
                    <option value="extreme">{t("Extreme")}</option>
                  </select>
                </label>
                {isTripoHSeries && (
                  <label className="block">
                    <span className="label">{t("Geometry quality")}</span>
                    <select
                      className="field"
                      value={settings.geometryQuality ?? ""}
                      onChange={(event) =>
                        update({
                          geometryQuality: (event.target.value ||
                            undefined) as typeof settings.geometryQuality,
                        })
                      }
                    >
                      <option value="">{t("Provider default")}</option>
                      <option value="standard">{t("Standard")}</option>
                      <option value="detailed">{t("Detailed")}</option>
                    </select>
                  </label>
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      isTripoP1
                        ? true
                        : (settings.texture ?? settings.pbr ?? false)
                    }
                    disabled={isTripoP1}
                    onChange={(event) =>
                      update({
                        texture: event.target.checked,
                        pbr: event.target.checked,
                      })
                    }
                  />
                  {t("Generate textures")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.pbr ?? false}
                    onChange={(event) =>
                      update({
                        pbr: event.target.checked,
                        texture: event.target.checked
                          ? settings.texture
                          : false,
                      })
                    }
                  />
                  {t("PBR materials")}
                </label>
                {(isTripoHSeries || isTripoP1) && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.autoSize ?? false}
                      onChange={(event) =>
                        update({ autoSize: event.target.checked || undefined })
                      }
                    />
                    {t("Auto size")}
                  </label>
                )}
                {isTripoHSeries && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.smartLowPoly ?? false}
                      onChange={(event) =>
                        update({
                          smartLowPoly: event.target.checked || undefined,
                        })
                      }
                    />
                    {t("Smart low-poly")}
                  </label>
                )}
                {isTripoHSeries && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.generateParts ?? false}
                      onChange={(event) =>
                        update({
                          generateParts: event.target.checked || undefined,
                          ...(event.target.checked
                            ? { texture: false, pbr: false }
                            : {}),
                        })
                      }
                    />
                    {t("Generate separate parts")}
                  </label>
                )}
                {(isTripoHSeries || isTripoP1) && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.exportUv ?? true}
                      onChange={(event) =>
                        update({ exportUv: event.target.checked })
                      }
                    />
                    {t("Export UVs")}
                  </label>
                )}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.imageAutofix ?? false}
                    onChange={(event) =>
                      update({
                        imageAutofix: event.target.checked || undefined,
                      })
                    }
                  />
                  {t("Auto-fix reference image")}
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="label">{t("Image texture alignment")}</span>
                  <select
                    className="field"
                    value={settings.textureAlignment ?? ""}
                    onChange={(event) =>
                      update({
                        textureAlignment: (event.target.value ||
                          undefined) as typeof settings.textureAlignment,
                      })
                    }
                  >
                    <option value="">{t("Original image")}</option>
                    <option value="original_image">
                      {t("Original image")}
                    </option>
                    <option value="geometry">{t("Geometry")}</option>
                  </select>
                </label>
                <label className="block">
                  <span className="label">{t("Image orientation")}</span>
                  <select
                    className="field"
                    value={settings.orientation ?? ""}
                    onChange={(event) =>
                      update({
                        orientation: (event.target.value ||
                          undefined) as typeof settings.orientation,
                      })
                    }
                  >
                    <option value="">{t("Default")}</option>
                    <option value="default">{t("Default")}</option>
                    <option value="align_image">{t("Align to image")}</option>
                  </select>
                </label>
                {(isTripoHSeries || isTripoP1) && (
                  <label className="block">
                    <span className="label">{t("Compression")}</span>
                    <select
                      className="field"
                      value={settings.compress ?? ""}
                      onChange={(event) =>
                        update({
                          compress: (event.target.value ||
                            undefined) as typeof settings.compress,
                        })
                      }
                    >
                      <option value="">{t("None")}</option>
                      <option value="geometry">{t("Geometry")}</option>
                    </select>
                  </label>
                )}
              </div>
              <p className="pt-1 text-[11px] font-medium text-stone-600 dark:text-stone-300">
                {t("Model conversion")}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="label">{t("Texture format")}</span>
                  <select
                    className="field"
                    value={settings.textureFormat ?? ""}
                    onChange={(event) =>
                      update({
                        textureFormat: (event.target.value ||
                          undefined) as typeof settings.textureFormat,
                      })
                    }
                  >
                    <option value="">{t("Provider default")}</option>
                    <option value="PNG">{t("PNG")}</option>
                    <option value="JPEG">{t("JPEG")}</option>
                    <option value="WEBP">{t("WEBP")}</option>
                    <option value="TARGA">{t("TARGA")}</option>
                    <option value="TIFF">{t("TIFF")}</option>
                    <option value="BMP">{t("BMP")}</option>
                    <option value="HDR">{t("HDR")}</option>
                    <option value="DPX">{t("DPX")}</option>
                  </select>
                </label>
                <label className="block">
                  <span className="label">{t("FBX preset")}</span>
                  <select
                    className="field"
                    value={settings.fbxPreset ?? ""}
                    onChange={(event) =>
                      update({
                        fbxPreset: (event.target.value ||
                          undefined) as typeof settings.fbxPreset,
                      })
                    }
                  >
                    <option value="">{t("Provider default")}</option>
                    <option value="blender">{t("Blender")}</option>
                    <option value="3dsmax">{t("3ds Max")}</option>
                    <option value="mixamo">{t("Mixamo")}</option>
                  </select>
                </label>
                <label className="block">
                  <span className="label">{t("Scale factor")}</span>
                  <input
                    type="number"
                    className="field"
                    min={0.001}
                    max={100}
                    step={0.001}
                    value={settings.scaleFactor ?? ""}
                    placeholder="1"
                    onChange={(event) =>
                      update({
                        scaleFactor: event.target.value
                          ? Number(event.target.value)
                          : undefined,
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="label">{t("Flatten threshold")}</span>
                  <input
                    type="number"
                    className="field"
                    min={0}
                    max={100}
                    step={0.1}
                    value={settings.flattenBottomThreshold ?? ""}
                    placeholder={t("Provider default")}
                    onChange={(event) =>
                      update({
                        flattenBottomThreshold: event.target.value
                          ? Number(event.target.value)
                          : undefined,
                      })
                    }
                  />
                </label>
              </div>
              <label className="block">
                <span className="label">{t("Part names")}</span>
                <input
                  className="field"
                  value={settings.partNames ?? ""}
                  placeholder={t("Comma-separated names")}
                  onChange={(event) =>
                    update({ partNames: event.target.value || undefined })
                  }
                />
              </label>
              <div className="flex flex-wrap gap-4 text-xs">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.bake ?? false}
                    onChange={(event) =>
                      update({ bake: event.target.checked || undefined })
                    }
                  />
                  {t("Bake textures")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.packUv ?? false}
                    onChange={(event) =>
                      update({ packUv: event.target.checked || undefined })
                    }
                  />
                  {t("Pack UVs")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.exportVertexColors ?? false}
                    onChange={(event) =>
                      update({
                        exportVertexColors: event.target.checked || undefined,
                      })
                    }
                  />
                  {t("Export vertex colors")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.pivotToCenterBottom ?? false}
                    onChange={(event) =>
                      update({
                        pivotToCenterBottom: event.target.checked || undefined,
                      })
                    }
                  />
                  {t("Pivot to center bottom")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.flattenBottom ?? false}
                    onChange={(event) =>
                      update({
                        flattenBottom: event.target.checked || undefined,
                      })
                    }
                  />
                  {t("Flatten bottom")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.forceSymmetry ?? false}
                    onChange={(event) =>
                      update({
                        forceSymmetry: event.target.checked || undefined,
                      })
                    }
                  />
                  {t("Force symmetry")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.withAnimation ?? false}
                    onChange={(event) =>
                      update({
                        withAnimation: event.target.checked || undefined,
                      })
                    }
                  />
                  {t("Include animation")}
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="label">{t("Export orientation")}</span>
                  <select
                    className="field"
                    value={settings.exportOrientation ?? ""}
                    onChange={(event) =>
                      update({
                        exportOrientation: (event.target.value ||
                          undefined) as typeof settings.exportOrientation,
                      })
                    }
                  >
                    <option value="">{t("Provider default")}</option>
                    <option value="+x">+X</option>
                    <option value="-x">-X</option>
                    <option value="-y">-Y</option>
                    <option value="+y">+Y</option>
                  </select>
                </label>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-4 text-xs">
            {!isTripoRigging && !isTripoAnimation && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.rig ?? false}
                  onChange={(event) =>
                    update({ rig: event.target.checked || undefined })
                  }
                />
                {t("Auto-rig humanoid")}
              </label>
            )}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.lod ?? false}
                onChange={(event) =>
                  update({ lod: event.target.checked || undefined })
                }
              />
              {t("Request LOD metadata")}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={
                  settings.collision !== "none" && Boolean(settings.collision)
                }
                onChange={(event) =>
                  update({ collision: event.target.checked ? "box" : "none" })
                }
              />
              {t("Include collision hint")}
            </label>
          </div>
        </div>
      )}
    </>
  );
}
